/**
 * @file ContentPlacementField.tsx
 * @description «Содержимое в блоке» — положение колонок внутри секции
 * (phase19.16, партия SB120, этап 1; uiux-sb120-stage1 «Дополнение» §2.8;
 * данные db-design-sb120-stage1 §1 `look.place` / `look.placePhone`).
 *
 * Сетка 3×3 и «Растянуть по высоте» (`PlaceGrid`). По вертикали — неактивно
 * при высоте «По содержимому» (двигать некуда), по горизонтали — при ширине
 * «Во всю». ПК: нет ключа — по центру и по центру (section.css, `mx-auto`);
 * совпавшее с умолчанием не пишется. Телефон: нет ключа — как на ПК.
 */

import { type SectionLook, type SectionPlace, type SectionPlaceX, type SectionPlaceY } from "@chaiberi/shared";
import { RotateCcw } from "lucide-react";

import { Button } from "../../../ui/button";
import { FieldHint } from "../../../ui/settings-groups";
import { PlaceGrid } from "../fields/PlaceGrid";
import { PLACE_DEFAULT, placeXDisabled, placeYDisabled, type Device } from "../../../../lib/site-builder/look-size";

interface Props {
  look: SectionLook;
  device: Device;
  onChange: (look: SectionLook) => void;
}

/** ПК: значения по умолчанию не храним (нет ключа = по центру). */
function compact(place: { y: SectionPlaceY; x: SectionPlaceX }): SectionPlace | undefined {
  const out: SectionPlace = {};
  if (place.y !== PLACE_DEFAULT.y) out.y = place.y;
  if (place.x !== PLACE_DEFAULT.x) out.x = place.x;
  return Object.keys(out).length > 0 ? out : undefined;
}

export function ContentPlacementField({ look, device, onChange }: Props) {
  const phone = device === "phone";
  const desktop = { ...PLACE_DEFAULT, ...look.place };
  const value = phone ? { ...desktop, ...look.placePhone } : desktop;
  const height = phone ? (look.heightPhone ?? look.height) : look.height;
  const yOff = placeYDisabled(height);
  const xOff = placeXDisabled(look);

  function set(place: { y: SectionPlaceY; x: SectionPlaceX } | undefined) {
    const next = { ...look };
    const key = phone ? "placePhone" : "place";
    const stored = phone ? place : place && compact(place);
    if (stored) next[key] = stored;
    else delete next[key];
    onChange(next);
  }

  return (
    <div className="flex min-w-0 flex-col gap-1">
      <div className="flex min-h-8 items-center justify-between gap-2">
        <span className="sr-only">{phone ? "Содержимое в блоке на телефоне" : "Содержимое в блоке"}</span>
        {phone && look.placePhone ? (
          <Button variant="ghost" size="sm" className="ml-auto h-12 can-hover:h-8" onClick={() => set(undefined)} aria-label="Содержимое на телефоне: как на ПК">
            <RotateCcw aria-hidden />
            как на ПК
          </Button>
        ) : phone ? (
          <span className="type-caption ml-auto text-on-surface-variant">как на ПК</span>
        ) : null}
      </div>
      <PlaceGrid label={phone ? "Содержимое в блоке на телефоне" : "Содержимое в блоке"} y={value.y} x={value.x} yDisabled={yOff} xDisabled={xOff} onChange={set} />
      {value.y === "stretch" && !yOff ? (
        <FieldHint className="mt-0">Растянуть: колонки без своего положения занимают всю высоту блока — общее «По вертикали» в «Колонках» тогда не действует.</FieldHint>
      ) : null}
      {yOff && xOff ? (
        <FieldHint className="mt-0">Блок во всю ширину и высотой по содержимому — двигать некуда. Выберите высоту «По фото», «Числом» или «Экран».</FieldHint>
      ) : yOff ? (
        <FieldHint className="mt-0">Блок высотой по содержимому — по вертикали двигать некуда. Выберите высоту «По фото», «Числом» или «Экран».</FieldHint>
      ) : xOff ? (
        <FieldHint className="mt-0">Блок во всю ширину — по горизонтали двигать некуда.</FieldHint>
      ) : (
        <FieldHint className="mt-0">По горизонтали двигается полоса ширины блока внутри фона во всю ширину.</FieldHint>
      )}
    </div>
  );
}
